/**
 * Celebration Animations
 * Shared animation effects for celebrations (voice detection results, discounts, etc.)
 */

import type { MusicEmoji, CelebrationEmoji, ConfettiColor } from '../types/paywall.types';

export const MUSIC_EMOJIS: MusicEmoji[] = [
  '🎵',
  '🎶',
  '🎼',
  '🎤',
  '🎸',
  '🎹',
  '🎺',
  '🎷',
  '🥁',
  '🎻',
  '🪕',
  '🪗',
];

export const CELEBRATION_EMOJIS: CelebrationEmoji[] = ['🎉', '🎊', '✨', '🌟', '💫', '⭐'];

export const CONFETTI_COLORS: ConfettiColor[] = [
  '#7827E6',
  '#8D39EC',
  '#AA4FF6',
  '#EA80FC',
  '#FFB140',
  '#67D5B5',
];

/**
 * Create a fullscreen overlay for celebration animations
 */
export function createCelebrationOverlay(): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'congratulations-overlay';
  document.body.appendChild(overlay);
  return overlay;
}

/**
 * Create music emoji fireworks falling from top
 */
export function createMusicFireworks(container: HTMLElement): void {
  const particleCount = 35;

  for (let i = 0; i < particleCount; i++) {
    setTimeout(() => {
      const particle = document.createElement('div');
      particle.className = 'firework-particle music-particle';

      // Mix music and celebration emojis
      const allEmojis = [...MUSIC_EMOJIS, ...CELEBRATION_EMOJIS];
      const emoji = allEmojis[Math.floor(Math.random() * allEmojis.length)];
      particle.textContent = emoji || '🎵';

      // Random starting position along the top
      particle.style.left = `${Math.random() * 100}%`;
      particle.style.top = `-50px`;

      // Random size for variety
      const scale = 0.6 + Math.random() * 0.8;
      particle.style.fontSize = `${scale * 30}px`;

      // Random drift during fall
      const driftX = (Math.random() - 0.5) * 250;
      particle.style.setProperty('--drift-x', `${driftX}px`);

      // Random rotation
      const rotation = Math.random() * 720 - 360;
      particle.style.setProperty('--rotation', `${rotation}deg`);

      // Random animation duration
      const duration = 3 + Math.random() * 2;
      particle.style.animationDuration = `${duration}s`;

      // Random delay for staggered effect
      particle.style.animationDelay = `${Math.random() * 0.5}s`;

      container.appendChild(particle);

      // Remove after animation
      setTimeout(() => {
        particle.remove();
      }, (duration + 0.5) * 1000);
    }, i * 100);
  }
}

/**
 * Create confetti particles falling from top
 */
export function createConfetti(container: HTMLElement): void {
  const confettiCount = 50;

  for (let i = 0; i < confettiCount; i++) {
    setTimeout(() => {
      const confetti = document.createElement('div');
      confetti.className = 'firework-particle confetti-particle';
      const color = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
      confetti.style.backgroundColor = color || '#7827E6';

      // Random starting position
      confetti.style.left = `${Math.random() * 100}%`;
      confetti.style.top = `-20px`;

      // Random size
      const width = 5 + Math.random() * 10;
      const height = 10 + Math.random() * 15;
      confetti.style.width = `${width}px`;
      confetti.style.height = `${height}px`;

      // Random drift
      const driftX = (Math.random() - 0.5) * 200;
      confetti.style.setProperty('--drift-x', `${driftX}px`);

      // Random animation duration
      const duration = 2.5 + Math.random() * 1.5;
      confetti.style.animationDuration = `${duration}s`;

      // Random delay
      confetti.style.animationDelay = `${Math.random() * 0.3}s`;

      // Random rotation
      confetti.style.transform = `rotate(${Math.random() * 360}deg)`;

      container.appendChild(confetti);

      // Remove after animation
      setTimeout(() => {
        confetti.remove();
      }, (duration + 0.3) * 1000);
    }, i * 60);
  }
}

/**
 * Create sparkle particles
 */
export function createSparkles(container: HTMLElement): void {
  const sparkleCount = 30;

  for (let i = 0; i < sparkleCount; i++) {
    setTimeout(() => {
      const sparkle = document.createElement('div');
      sparkle.className = 'firework-particle sparkle-particle';

      // Random starting position
      sparkle.style.left = `${Math.random() * 100}%`;
      sparkle.style.top = `${Math.random() * 30}%`;

      // Random delay
      sparkle.style.animationDelay = `${Math.random() * 2}s`;

      container.appendChild(sparkle);

      // Remove after animation
      setTimeout(() => {
        sparkle.remove();
      }, 3000);
    }, i * 100);
  }
}

/**
 * Create floating musical notes rising from bottom
 */
export function createFloatingNotes(container: HTMLElement): void {
  const noteCount = 15;
  const notes = ['♪', '♫', '♬', '🎵', '🎶'];

  for (let i = 0; i < noteCount; i++) {
    setTimeout(() => {
      const note = document.createElement('div');
      note.className = 'firework-particle floating-note';
      const noteChar = notes[Math.floor(Math.random() * notes.length)];
      note.textContent = noteChar || '♪';

      // Random position
      note.style.left = `${10 + Math.random() * 80}%`;
      note.style.bottom = '-50px';

      // Random size
      const scale = 0.5 + Math.random() * 0.5;
      note.style.fontSize = `${scale * 40}px`;

      // Random float speed
      const duration = 4 + Math.random() * 3;
      note.style.setProperty('--float-duration', `${duration}s`);

      // Random sway amount
      const swayAmount = 30 + Math.random() * 40;
      note.style.setProperty('--sway-amount', `${swayAmount}px`);

      container.appendChild(note);

      // Remove after animation
      setTimeout(() => {
        note.remove();
      }, duration * 1000);
    }, i * 200);
  }
}

export interface CelebrationOptions {
  /** Duration before auto-cleanup in ms (default: 5000) */
  duration?: number;
  /** Whether to include music fireworks (default: true) */
  musicFireworks?: boolean;
  /** Whether to include confetti (default: true) */
  confetti?: boolean;
  /** Whether to include sparkles (default: true) */
  sparkles?: boolean;
  /** Whether to include floating notes (default: true) */
  floatingNotes?: boolean;
}

/**
 * Play a celebration animation with all effects
 * @returns cleanup function to manually stop the animation
 */
export function playCelebration(options: CelebrationOptions = {}): () => void {
  const {
    duration = 5000,
    musicFireworks = true,
    confetti = true,
    sparkles = true,
    floatingNotes = true,
  } = options;

  const overlay = createCelebrationOverlay();

  if (musicFireworks) createMusicFireworks(overlay);
  if (confetti) createConfetti(overlay);
  if (sparkles) createSparkles(overlay);
  if (floatingNotes) createFloatingNotes(overlay);

  // Auto-cleanup after duration
  const timeoutId = setTimeout(() => {
    overlay.classList.add('fade-out');
    setTimeout(() => {
      overlay.remove();
    }, 300);
  }, duration);

  // Return cleanup function
  return () => {
    clearTimeout(timeoutId);
    overlay.remove();
  };
}
